import { useTranslation } from 'react-i18next';
import { FileText, Image, Music } from 'lucide-react';
import { Text } from '../../base/Text';
import { IconButton } from '../../base/IconButton';
import styles from './FileItem.module.css';

export interface FileItemData {
  id: string;
  name: string;
  mimeType: string;
  fileType: 'document' | 'image' | 'audio';
  fileSize: number;
  storagePath: string;
  thumbnailPath?: string | null;
  createdAt: string;
}

interface FileItemProps {
  file: FileItemData;
  imageUrl?: string | null;
  onPress?: (file: FileItemData) => void;
  onMenuPress?: (file: FileItemData) => void;
}

const formatTimeAgo = (
  dateString: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) return t('links.justNow');
  if (diffInSeconds < 3600) return t('links.minutesAgo', { count: Math.floor(diffInSeconds / 60) });
  if (diffInSeconds < 86400) return t('links.hoursAgo', { count: Math.floor(diffInSeconds / 3600) });
  if (diffInSeconds < 604800) return t('links.daysAgo', { count: Math.floor(diffInSeconds / 86400) });
  return date.toLocaleDateString();
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileItem = ({ file, imageUrl, onPress, onMenuPress }: FileItemProps) => {
  const { t } = useTranslation();
  const Icon = file.fileType === 'image' ? Image : file.fileType === 'audio' ? Music : FileText;

  return (
    <div
      className={styles.card}
      onClick={() => onPress?.(file)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onPress?.(file)}
    >
      <div className={styles.thumbnail}>
        {file.fileType === 'image' && imageUrl ? (
          <img src={imageUrl} alt="" className={styles.thumbnailImage} />
        ) : (
          <div className={styles.iconPlaceholder}>
            <Icon size={32} className={styles.icon} />
          </div>
        )}
      </div>
      <div className={styles.content}>
        <Text variant="body" className={styles.name}>
          {file.name}
        </Text>
        <Text variant="meta" className={styles.meta}>
          {formatFileSize(file.fileSize)} · {formatTimeAgo(file.createdAt, t)}
        </Text>
      </div>
      {onMenuPress && (
        <IconButton
          variant="ghost"
          size="sm"
          label="Menu"
          onClick={(e) => {
            e.stopPropagation();
            onMenuPress(file);
          }}
        >
          ⋮
        </IconButton>
      )}
    </div>
  );
};
